import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
} from "@angular/core";
import { SbAsins, SbCreativeType, State } from "@front/m19-api-client";
import { IAlertComponent, IBadgeComponent } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { CreativePreviewAssets, CreativePreviewComponent } from "@m19-board/shared/creative-preview.component";

type CreativePreviewItem = {
  id: number;
  labelKey: number;
  selected: boolean;
  asins: SbAsins;
};

@Component({
  selector: "app-creative-preview-list",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, IAlertComponent, IBadgeComponent, CreativePreviewComponent],
  host: {
    class: "flex h-full min-h-0 flex-col",
  },
  templateUrl: "./creative-preview-list.component.html",
})
export class CreativePreviewListComponent {
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly creativeSbAsins = input<SbAsins[]>([]);
  readonly selectedCreativeId = input<number>(0);
  readonly creativeType = input<SbCreativeType>(SbCreativeType.productCollection);
  readonly brandName = input<string>();
  readonly headline = input<string>();
  readonly brandLogoUrl = input<string>();
  readonly videoUrl = input<string>();

  readonly assets = computed<CreativePreviewAssets>(() => {
    return {
      logoAssetId: this.brandLogoUrl(),
      headline: this.headline(),
      brandName: this.brandName(),
      state: State.OFF,
      videoAssetId: this.videoUrl(),
    };
  });

  readonly creatives = computed<CreativePreviewItem[]>(() => {
    const selectedId = this.selectedCreativeId();
    const source = this.creativeSbAsins().length > 0 ? this.creativeSbAsins() : [{ asin1: "" }];

    const items = source.map((sbAsins, index) => ({
      id: index,
      labelKey: index + 1,
      selected: index === selectedId,
      asins: sbAsins,
    }));

    return items;
  });

  constructor() {
    afterRenderEffect({
      write: () => {
        const selectedId = this.selectedCreativeId();
        const el = this.host.nativeElement.querySelector(`[data-creative-id="${selectedId}"]`);
        const scrollParent = el?.closest(".scrollbar");
        if (!(el instanceof HTMLElement) || !(scrollParent instanceof HTMLElement)) return;

        const offset = 20;
        const parentRect = scrollParent.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();

        let delta = 0;
        if (elRect.top < parentRect.top + offset) {
          delta = elRect.top - parentRect.top - offset;
        } else if (elRect.bottom > parentRect.bottom - offset) {
          delta = elRect.bottom - parentRect.bottom + offset;
        }

        if (delta !== 0) {
          scrollParent.scrollBy({ top: delta, behavior: "smooth" });
        }
      },
    });
  }
}
